import React, {useEffect, useState, useContext, useCallback, useMemo} from "react";
import {fetchUserCompanyLogo, getCompanyFeatureFlagsAPI, getCompanySMSQuotaAPI, getCompanyDataAPI} from "../Services/api";
import {UserReactContext} from "./UserContext";
import { isEqual } from 'lodash';

export const CompanyReactContext = React.createContext({
    companyLogoUrl: '',
    companyName: '',
    companyUsersPrefix: '',
    countryCode: '',
    refreshLogoUrl: () => {},
    refreshCompanyQuota: () => {},
    featureFlags: {},
    remainingQuota: 0,
    sentQuota: 0,
    lastSMSDate: null,
});


export const CompanyContextProvider = ({children}) => {
    const userContext = useContext(UserReactContext)
    const [companyLogoUrl, setCompanyLogoUrl] = useState('')
    const [companyName, setCompanyName] = useState('')
    const [companyUsersPrefix, setCompanyUsersPrefix] = useState('')
    const [countryCode, setCountryCode] = useState('')
    const [featureFlags, setFeatureFlags] = useState({})
    const [remainingQuota, setRemainingQuota] = useState(0)
    const [sentQuota, setSentQuota] = useState(0)
    const [lastSMSDate, setLastSMSDate] = useState(null)

    /**
     * Refresh company SMS quota values from server and update local state.
     * @returns {Promise<void>}
     */
    const refreshCompanyQuota = useCallback(async () => {
        getCompanySMSQuotaAPI({companyId: userContext.companyId})
            .then(({remainingQuota, sentMessages, lastSMSDate}) => {
                setRemainingQuota(remainingQuota)
                setSentQuota(sentMessages)
                setLastSMSDate(lastSMSDate)
            })
    }, [userContext.companyId])

    /**
     * Force refreshes the company logo URL to bypass caches.
     * @returns {void}
     */
    const refreshLogoUrl = useCallback(() => {
        setCompanyLogoUrl(`${companyLogoUrl}?${Date.now()}`)
    }, [companyLogoUrl, userContext.companyId])
    /**
     * Triggers a feature flags fetch.
     * @returns {void}
     */
    const refreshFeatureFlags = useCallback(() => {
        fetchFeatureFlags()
    }, [])

    const fetchCompanyLogo = async () => {
        if(userContext.isUserValid()){
            const logo = await fetchUserCompanyLogo()
            setCompanyLogoUrl(logo)
        }
    }

    const fetchCompanyData = async () => {
        try {
            const companyData = await getCompanyDataAPI()
            setCompanyName(companyData.companyName || '')
            setCompanyUsersPrefix(companyData.companyUsersPrefix || '')
            setCountryCode(companyData.countryCode || '')
        } catch (e) {
            console.error('Error fetching company data', e)
        }
    }
    const fetchFeatureFlags = async () => {
        try{
            const flags = await getCompanyFeatureFlagsAPI()
            // Avoid unnecessary re-renders by only updating when flags actually change
            setFeatureFlags((prev) => (isEqual(prev, flags) ? prev : flags))
        }
        catch(e){
            console.error('Error fetching feature flags', e)
        }
    }
    useEffect(() => {
        if(userContext.isUserValid()){
            fetchCompanyLogo()
            fetchCompanyData()
        }
    }, [userContext.user]);

    //useEffect to get feature flags every 30 seconds
    useEffect(() => {
        if(userContext.isUserValid()){
            fetchFeatureFlags()
            const interval = setInterval(() => {
                fetchFeatureFlags()
            }, 60000 * 4);
            return () => clearInterval(interval);
        }
    }, [userContext.user]);
    const contextValue = useMemo(() => ({
        companyLogoUrl,
        companyName,
        companyUsersPrefix,
        countryCode,
        refreshLogoUrl,
        featureFlags,
        refreshFeatureFlags,
        refreshCompanyQuota,
        remainingQuota,
        sentQuota,
        lastSMSDate
    }), [companyLogoUrl, companyName, companyUsersPrefix, countryCode, refreshLogoUrl, featureFlags, refreshFeatureFlags, refreshCompanyQuota, remainingQuota, sentQuota, lastSMSDate])

    return <CompanyReactContext.Provider value={contextValue}>
        {children}
    </CompanyReactContext.Provider>
}
